import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, MessageCircle, Loader2, CheckCircle2, Send, Navigation } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", mobile: "", type: "Enquiry", subject: "", message: "" };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const digits = (s = "") => String(s).replace(/\D/g, "");
const MAP_RE = /^https:\/\/(www\.google\.com\/maps|maps\.google\.com\/)/;
const LINK_RE = /^https:\/\/(www\.google\.com\/maps|maps\.google\.com\/|maps\.app\.goo\.gl\/)/;
const DEFAULT_MAP_EMBED = "https://www.google.com/maps?q=34MP%2BMPH+Hatha+Vidya+Charitable+Trust,+Gandhidham,+Gujarat+370201&output=embed";
const DEFAULT_MAP_LINK = "https://maps.app.goo.gl/p3dubKDPqN1ZLvBEA?g_st=aw";
export const waLink = (n) => { const d = digits(n); return d ? `https://wa.me/${d.length === 10 ? `91${d}` : d}` : ""; };

export default function ContactPage() {
  const { t } = useLang();
  const [c, setC] = useState({});
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const sending = useRef(false);

  useEffect(() => { api.get("/public/content/contact").then((r) => setC(r.data || {})).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending.current) return;
    if (!EMAIL_RE.test(form.email.trim())) { toast.error(t("Please enter a valid email address.")); return; }
    sending.current = true;
    setLoading(true);
    try { await api.post("/public/enquiries", { ...form, email: form.email.trim() }); setDone(true); setForm(empty); toast.success(t("Message Sent!")); }
    catch (err) {
      const msg = err.response ? formatApiError(err.response.data?.detail) : "";
      toast.error(err.response?.status === 422 ? t("Please enter a valid email address.") : (msg && err.response?.status === 429 ? msg : t("Could not send. Please check your connection and try again.")));
    }
    finally { sending.current = false; setLoading(false); }
  };

  const info = [
    { icon: MapPin, label: "Address", value: c.address || "Gandhidham, Kutch, Gujarat, India", href: null },
    { icon: Phone, label: "Phone", value: c.phone || "", href: c.phone ? `tel:+91${digits(c.phone).slice(-10)}` : null },
    { icon: Mail, label: "Email", value: c.email || "", href: c.email ? `mailto:${c.email}` : null },
    { icon: MessageCircle, label: "WhatsApp", value: c.whatsapp || "", href: waLink(c.whatsapp), external: true },
  ].filter((i) => i.value);

  return (
    <div data-testid="public-contact-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Reach Us")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("Contact Us")}</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid lg:grid-cols-2 gap-12">
        <div className="min-w-0">
          <h2 className="font-display text-3xl font-semibold text-[#194324]">{t("Get in touch")}</h2>
          <div className="mt-6 space-y-4">
            {info.map((i) => {
              const body = (
                <>
                  <div className="h-10 w-10 rounded-lg bg-[#E8F0EA] flex items-center justify-center shrink-0"><i.icon className="h-5 w-5 text-[#194324]" /></div>
                  <div className="min-w-0"><div className="text-xs font-mono uppercase tracking-widest text-[#B38728]">{t(i.label)}</div><div className={`text-[#194324] font-medium ${i.label === "Email" ? "break-all" : "[overflow-wrap:anywhere]"} ${i.href ? "underline decoration-[#C59B27]/50 underline-offset-4" : ""}`}>{i.value}</div></div>
                </>
              );
              const cls = "flex items-start gap-4 bg-white rounded-xl border border-[#E2DAD0] p-4";
              return i.href
                ? <a key={i.label} href={i.href} {...(i.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className={`${cls} hover:border-[#C59B27] transition-colors`} data-testid={`contact-link-${i.label.toLowerCase()}`}>{body}</a>
                : <div key={i.label} className={cls} data-testid={`contact-info-${i.label.toLowerCase()}`}>{body}</div>;
            })}
          </div>
          {MAP_RE.test(c.map_embed || DEFAULT_MAP_EMBED) && (
            <div className="mt-6 rounded-xl overflow-hidden border border-[#E2DAD0]" data-testid="contact-map">
              <iframe title="map" src={c.map_embed || DEFAULT_MAP_EMBED} className="w-full h-64 md:h-80" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          )}
          {LINK_RE.test(c.map_link || DEFAULT_MAP_LINK) && (
            <a href={c.map_link || DEFAULT_MAP_LINK} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#194324] hover:bg-[#2D623A] text-white px-5 py-2.5 text-sm font-semibold transition-colors" data-testid="contact-get-directions-link">
              <Navigation className="h-4 w-4" /> {t("Get Directions")}
            </a>
          )}
        </div>

        <div className="min-w-0">
          {done ? (
            <div className="bg-white rounded-2xl border border-[#E2DAD0] p-8 text-center" data-testid="contact-success">
              <div className="inline-flex h-16 w-16 rounded-full bg-[#E8F0EA] items-center justify-center mb-4"><CheckCircle2 className="h-8 w-8 text-[#194324]" /></div>
              <h3 className="font-display text-2xl font-semibold text-[#194324]">{t("Message Sent!")}</h3>
              <p className="text-[#5C7264] mt-2">{t("Thank you for reaching out. The trust will get back to you soon.")}</p>
              <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-full">{t("Send another")}</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E2DAD0] p-6 sm:p-8 space-y-4">
              <h3 className="font-display text-2xl font-semibold text-[#194324]">{t("Send a message")}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>{t("Name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} data-testid="contact-form-name" /></div>
                <div className="space-y-2"><Label>{t("Mobile")}</Label><Input type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} maxLength={20} data-testid="contact-form-mobile" /></div>
              </div>
              <div className="space-y-2"><Label>{t("Email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required maxLength={254} data-testid="contact-form-email" /></div>
              <div className="space-y-2">
                <Label>{t("Type")}</Label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="contact-form-type">
                  {["Enquiry", "Complaint", "Suggestion"].map((o) => <option key={o} value={o}>{t(o)}</option>)}
                </select>
              </div>
              <div className="space-y-2"><Label>{t("Subject")}</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={200} data-testid="contact-form-subject" /></div>
              <div className="space-y-2"><Label>{t("Message")}</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} required maxLength={5000} data-testid="contact-form-message" /></div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#194324] hover:bg-[#2D623A] text-white font-semibold" data-testid="contact-form-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> {t("Send Message")}</>}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
